"use client";

import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";

import { type SearchPassengers } from "@trip/constants";
import { type DateRange } from "@trip/utils";

interface SearchFormData {
  dateRange: DateRange | null;
  location: string | null;
  passengers: SearchPassengers;
}

export function useSearchForm({
  location,
  dateRange,
  passengers,
  tourType,
}: {
  dateRange?: DateRange;
  location?: string;
  passengers?: SearchPassengers;
  tourType?: string;
}) {
  const router = useRouter();
  const form = useForm<SearchFormData>({
    defaultValues: {
      dateRange: dateRange ?? null,
      location: location ?? null,
      passengers: passengers ?? { adults: 2, children: [] },
    },
  });

  const submit = form.handleSubmit(
    async ({ dateRange, location, passengers }) => {
      const locationParams = location ? ["location", location] : [];
      const urlSearchParamsInit = [
        dateRange ? ["start", dateRange.start.toString()] : undefined,
        dateRange ? ["end", dateRange.end.toString()] : undefined,
        tourType ? ["type", tourType] : undefined,
        ["adults", passengers.adults.toString()],
        locationParams,
        ...passengers.children.map((child) => [
          "children",
          JSON.stringify(child),
        ]),
      ].flatMap((entity) => (entity?.length === 2 ? [entity] : []));
      const searchParams = new URLSearchParams(urlSearchParamsInit);
      const url = `/search?${searchParams.toString()}`;
      router.push(url);
    },
  );

  return {
    ...form,
    submit,
    fields: {
      dateRange: {
        value: form.watch("dateRange"),
        onChange: (value: DateRange | null) => {
          form.setValue("dateRange", value);
        },
      },
      location: {
        value: form.watch("location"),
        onChange: (value: string | null) => {
          form.setValue("location", value);
        },
      },
      passengers: {
        value: form.watch("passengers"),
        onChange: (value: SearchPassengers) => {
          form.setValue("passengers", value);
        },
      },
    },
  };
}

export type Form = ReturnType<typeof useSearchForm>;
